/**
 * The proposal as the client receives it: the branded, printable document.
 *
 * The merge fills a body; this wraps that body in the studio's own layout — the
 * warm ground, the red rule, the display headline over serif text, the phase
 * numerals, the page furniture — so what goes out looks like the InDesign
 * original rather than a text file.
 *
 * The stylesheet is taken from two documents that already exist: the Viking
 * Automotive proposal and the tokenised template built from it. Same palette,
 * same two typefaces, same page furniture, so a generated document and a
 * hand-set one can sit in the same folder without one of them looking wrong.
 *
 * Rendered as HTML and printed by the browser rather than drawn with a PDF
 * library. A 14-page designed document has multi-column pages, a full-bleed
 * cover and a phase rail; reproducing that by placing coordinates would be a
 * worse copy that also has to be maintained twice. The browser already lays
 * this out, and its Save as PDF is the same engine that renders the preview.
 */

import { LOOK_CSS } from './proposalPages/css'
import { LOOKS } from './proposalLooks'

export const BRAND = {
  red: '#EF4237',
  ink: '#1A1A18',
  navy: '#112B32',
  mute: '#6B6B69',
  rule: '#D8D3C8',
  /** The warm ground both documents sit on. Not white — white reads colder. */
  paper: '#F4F0E9',
  /** The callout blocks: technical notes, platform fees, value bundles. */
  tan: '#E7DFD3',
  /** Small uppercase labels — lighter than body, never as light as a hint. */
  label: '#8A8780',
}

/**
 * Where the typefaces come from.
 *
 * The print window is written from scratch with `document.write`, so it
 * inherits nothing from the portal — relative URLs do not resolve and the
 * app's own stylesheet is not there, so the source is stated absolutely.
 *
 * Public Sans everywhere (redesign, Phase 6): headings and furniture are set
 * in it, as the portal is. The old display face is no longer loaded.
 *
 * The serif is the one substitution. Both documents are set in a licensed
 * text face; Source Serif is the closest thing that can be linked from a
 * document that has to open on somebody else's machine.
 */
export const DOCUMENT_FONTS = [
  'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&family=Public+Sans:wght@400;500;600;700;800;900&display=swap',
]

const DISPLAY = `'Public Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif`
const TEXT = `'Source Serif 4', Georgia, 'Times New Roman', serif`
const UI = `'Public Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif`

/** The stylesheet. Print-first: the screen preview is the same document. */
export const DOCUMENT_CSS = `
  @page { size: letter portrait; margin: 0; }
  * { box-sizing: border-box; }
  body {
    margin: 0; background: ${BRAND.paper};
    font-family: ${TEXT};
    color: ${BRAND.ink}; -webkit-print-color-adjust: exact; print-color-adjust: exact;
    text-wrap: pretty;
  }
  h1, h2, h3, h4 { text-wrap: balance; }
  .page {
    width: 8.5in; min-height: 11in; padding: 0.85in 0.95in 1in;
    margin: 0 auto 18px; background: ${BRAND.paper}; position: relative;
    page-break-after: always; overflow: hidden;
  }
  .page:last-child { page-break-after: auto; }
  @media print { .page { margin: 0; box-shadow: none; } }
  @media screen { .page { box-shadow: 0 2px 14px rgba(0,0,0,0.10); } }

  /* ── Page furniture ──────────────────────────────────────────────────── */

  /* The black hairline every interior page opens with. */
  .runhead {
    border-top: 2.5px solid ${BRAND.ink};
    padding-top: 13px; margin-bottom: 30px; min-height: 13px;
    font-family: ${UI}; font-size: 7.5pt; font-weight: 600;
    letter-spacing: 0.14em; text-transform: uppercase; color: ${BRAND.label};
  }
  .foot {
    position: absolute; left: 0.95in; right: 0.95in; bottom: 0.5in;
    display: flex; justify-content: space-between; align-items: baseline;
    border-top: 1px solid ${BRAND.rule}; padding-top: 9px;
    font-family: ${UI}; font-size: 7pt; font-weight: 600;
    letter-spacing: 0.14em; text-transform: uppercase; color: ${BRAND.label};
  }
  .foot .n { color: ${BRAND.red}; }

  /* ── Type ────────────────────────────────────────────────────────────── */

  .eyebrow {
    font-family: ${UI}; font-size: 8pt; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase; color: ${BRAND.red};
    margin-bottom: 10px;
  }
  h1 {
    font-family: ${DISPLAY}; font-size: 33pt; line-height: 1.06;
    margin: 0 0 20px; font-weight: 600; letter-spacing: -0.4pt;
  }
  h2 {
    font-family: ${DISPLAY}; font-size: 15pt; line-height: 1.2;
    margin: 26px 0 11px; font-weight: 600;
  }
  h3 {
    font-family: ${DISPLAY}; font-size: 11pt; margin: 20px 0 8px; font-weight: 600;
  }
  .accent { color: ${BRAND.red}; }
  p { font-size: 10.5pt; line-height: 1.7; margin: 0 0 12px; }
  .lead { font-size: 13pt; line-height: 1.5; }
  .cols { column-count: 2; column-gap: 30px; }
  .muted { color: ${BRAND.mute}; }
  .small { font-size: 9pt; line-height: 1.6; color: ${BRAND.mute}; }

  /* ── Cover ───────────────────────────────────────────────────────────── */

  .cover { padding-top: 0.75in; }
  .cover .bar {
    position: absolute; top: 0; left: 0; right: 0; height: 9px; background: ${BRAND.red};
  }
  .cover .org {
    font-family: ${UI}; font-size: 8pt; font-weight: 600;
    letter-spacing: 0.22em; text-transform: uppercase; color: ${BRAND.label};
    margin-bottom: 2.6in;
  }
  .cover h1 { font-size: 42pt; letter-spacing: -1pt; margin-bottom: 16px; }
  .cover .deck {
    font-size: 14pt; line-height: 1.45; color: ${BRAND.ink};
    max-width: 4.1in; margin: 0 0 34px;
  }
  /* The measured tick rule under the deck — a scale, not decoration: it is the
     one mark on the cover that says this was drawn rather than typed. */
  .ticks {
    display: flex; align-items: flex-end; justify-content: space-between;
    width: 6in; height: 22px; margin-bottom: 42px;
  }
  .ticks i { display: block; width: 1.5px; height: 8px; background: #B9B2A4; }
  .ticks i.tall { height: 22px; background: ${BRAND.red}; }
  /* Four columns that stay four columns. As flex with a gap, a long agency
     name pushed the last two onto a second row and the cover lost its grid. */
  .cover .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 26px; }
  .cover .meta .k {
    font-family: ${UI}; font-size: 7pt; font-weight: 600;
    letter-spacing: 0.16em; text-transform: uppercase; color: ${BRAND.label};
    margin-bottom: 7px;
  }
  .cover .meta .v { font-size: 10.5pt; line-height: 1.5; }
  .cover .fine {
    position: absolute; left: 0.95in; right: 0.95in; bottom: 0.7in;
    font-size: 7.5pt; line-height: 1.6; color: ${BRAND.label}; max-width: 5.6in;
  }

  /* ── The phase / numbered item block ─────────────────────────────────── */

  .phase { display: flex; gap: 30px; margin-bottom: 26px; page-break-inside: avoid; }
  .phase .rail { width: 1.5in; flex: none; }
  .phase .num {
    font-family: ${DISPLAY}; font-size: 40pt; font-weight: 600; line-height: 0.9;
    color: ${BRAND.red}; margin-bottom: 9px;
  }
  .phase .name {
    font-family: ${DISPLAY}; font-size: 11pt; font-weight: 600; line-height: 1.25;
    padding-bottom: 7px; border-bottom: 1px solid ${BRAND.ink};
  }
  .phase .when {
    font-family: ${UI}; font-size: 7.5pt; font-weight: 600;
    letter-spacing: 0.12em; text-transform: uppercase; color: ${BRAND.label};
    margin-top: 7px;
  }
  .phase .cost { font-family: ${DISPLAY}; font-size: 17pt; font-weight: 600; margin-top: 11px; }
  .phase .body { flex: 1; min-width: 0; }
  .phase .body p:first-child { margin-top: 0; }

  /* The red dash bullet both documents use instead of a disc. */
  .items { margin: 0; padding: 0; list-style: none; }
  .items li { position: relative; padding-left: 21px; margin-bottom: 11px; font-size: 10pt; line-height: 1.62; }
  .items li::before {
    content: ''; position: absolute; left: 0; top: 0.62em;
    width: 11px; height: 2.5px; background: ${BRAND.red};
  }
  .items b, .items strong { font-family: ${DISPLAY}; font-weight: 600; }

  /* ── Definition rows: a short label, a rule, then the detail ─────────── */

  .defs { margin: 18px 0; }
  .def { display: flex; gap: 16px; align-items: baseline; margin-bottom: 15px; page-break-inside: avoid; }
  .def .k { width: 1.45in; flex: none; font-family: ${DISPLAY}; font-size: 10pt; font-weight: 600; line-height: 1.3; }
  .def .r { width: 28px; flex: none; border-top: 1.5px solid ${BRAND.ink}; margin-top: 9px; }
  .def .v { flex: 1; font-size: 10pt; line-height: 1.62; }

  /* ── Tables ──────────────────────────────────────────────────────────── */

  table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 10pt; }
  th {
    text-align: left; padding: 0 11px 8px; border-bottom: 1.5px solid ${BRAND.ink};
    font-family: ${UI}; font-size: 7.5pt; font-weight: 600;
    letter-spacing: 0.14em; text-transform: uppercase; color: ${BRAND.label};
  }
  td { padding: 12px 11px; border-bottom: 1px solid ${BRAND.rule}; vertical-align: top; line-height: 1.5; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total td { border-bottom: none; font-family: ${DISPLAY}; font-weight: 600; }
  tr.total td.num { color: ${BRAND.red}; }
  td .sub { display: block; font-size: 9pt; color: ${BRAND.mute}; margin-top: 3px; }
  /* A schedule is a short list of figures, not a price table: tighter rows so
     an investment page with several lines still fits on one sheet. */
  table.compact { margin: 6px 0 10px; }
  table.compact td { padding: 6px 11px; font-size: 9.5pt; }

  /* ── Blocks ──────────────────────────────────────────────────────────── */

  /* The tan panel: technical notes, platform fees, value bundles. */
  .note {
    background: ${BRAND.tan}; padding: 17px 20px; margin: 20px 0;
    font-size: 9pt; line-height: 1.65;
  }
  .note h3 { margin: 0 0 7px; font-size: 10pt; }
  .note p { font-size: 9pt; line-height: 1.65; margin: 0 0 7px; color: #4A473F; }
  .note p:last-child { margin-bottom: 0; }

  /* The red-ruled pull quote. */
  .pull {
    border-left: 3px solid ${BRAND.red}; padding-left: 17px; margin: 22px 0;
    font-size: 11.5pt; line-height: 1.6;
  }
  .pull p { font-size: 11.5pt; line-height: 1.6; margin: 0; }

  .callout { background: ${BRAND.red}; color: #fff; padding: 22px 26px; margin: 24px -0.95in 0; }
  .callout p { font-family: ${DISPLAY}; font-size: 12.5pt; font-weight: 600; margin: 0; line-height: 1.45; }

  .rule-left { border-left: 3px solid ${BRAND.red}; padding-left: 14px; margin: 18px 0; font-size: 9.5pt; line-height: 1.6; }

  /* ── The ICON rail along the foot of a phase page ────────────────────── */

  .iconrail {
    position: absolute; left: 0; right: 0; bottom: 1.05in;
    background: #EAE4DA; padding: 13px 0.95in; display: flex; gap: 26px;
  }
  .iconrail .step { display: flex; gap: 9px; align-items: center; opacity: 0.42; }
  .iconrail .step.on { opacity: 1; }
  .iconrail .dot {
    width: 26px; height: 26px; border-radius: 50%; flex: none;
    border: 1.5px solid ${BRAND.mute}; color: ${BRAND.mute};
    display: flex; align-items: center; justify-content: center;
    font-family: ${DISPLAY}; font-size: 10pt; font-weight: 600;
  }
  .iconrail .step.on .dot { background: ${BRAND.red}; border-color: ${BRAND.red}; color: #fff; }
  .iconrail .t { font-family: ${DISPLAY}; font-size: 8.5pt; font-weight: 600; }
  .iconrail .w { font-family: ${UI}; font-size: 7pt; color: ${BRAND.mute}; }

  /* The oversized phase numeral behind a heading. */
  .numeral {
    position: absolute; top: 0.7in; right: 0.85in;
    font-family: ${DISPLAY}; font-size: 86pt; font-weight: 600;
    color: rgba(26,26,24,0.07); line-height: 1; z-index: 0;
  }
  .above { position: relative; z-index: 1; }

  /* ── Editing on the page ─────────────────────────────────────────────────
   *
   * The document is the editor. Nothing here renders unless the body carries
   * the editing class, so what prints is never marked up with affordances —
   * the same markup is both the canvas and the artefact.
   */

  body.editing [data-edit] {
    outline: 1px dashed transparent; outline-offset: 3px;
    border-radius: 2px; cursor: text; transition: outline-color 0.1s, background 0.1s;
  }
  body.editing [data-edit]:hover { outline-color: #B5AE9F; }
  body.editing [data-edit]:focus {
    outline: 1.5px solid ${BRAND.red}; outline-offset: 3px;
    background: rgba(239,66,55,0.05);
  }
  /* A value nobody has supplied, named rather than blank — a gap you can see
     is a gap that gets filled. */
  .token {
    background: #FBEFA8; padding: 0 4px; border-radius: 2px;
    color: #6F6A55; font-style: normal;
  }
  body.editing [data-add] {
    display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
    font-family: ${UI}; font-size: 8pt; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase; color: ${BRAND.red};
    border: 1px dashed ${BRAND.red}; border-radius: 3px; padding: 6px 12px; margin-top: 4px;
  }
  body.editing [data-drop] {
    position: absolute; right: -0.55in; top: 0; cursor: pointer;
    font-family: ${UI}; font-size: 12pt; line-height: 1; color: #B5AE9F; padding: 2px 5px;
  }
  body.editing [data-drop]:hover { color: ${BRAND.red}; }

  /* ── The letterhead ──────────────────────────────────────────────────── */

  .letterhead { display: flex; align-items: flex-start; gap: 15px; margin-bottom: 30px; }
  .letterhead .wordmark {
    font-family: ${DISPLAY}; font-size: 15pt; font-weight: 600; line-height: 1.05;
    letter-spacing: -0.2pt; text-transform: uppercase;
  }
  .letterhead .wordmark .second { color: ${BRAND.red}; }
  .letterhead .addr {
    margin-left: auto; text-align: right;
    font-family: ${UI}; font-size: 7.5pt; line-height: 1.7; color: ${BRAND.label};
  }

  /* ── Signature lines ─────────────────────────────────────────────────── */

  .sigs { display: flex; gap: 40px; margin-top: 46px; }
  .sig { flex: 1; }
  .sig .line { border-top: 1.5px solid ${BRAND.ink}; margin-bottom: 8px; }
  .sig .k {
    font-family: ${UI}; font-size: 7pt; font-weight: 600;
    letter-spacing: 0.16em; text-transform: uppercase; color: ${BRAND.label};
  }
  .sig .v { font-size: 10pt; margin-top: 4px; }
${LOOK_CSS}`

export const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * Plain text becomes plain pages.
 *
 * A template written as prose still has to print as something. Blank lines
 * separate paragraphs, and a line of dashes starts a new page — enough
 * structure to be useful without pretending a text file is a designed document.
 */
export function textToPages(body) {
  const chunks = String(body || '').split(/\n\s*-{3,}\s*\n/)
  return chunks.map(chunk =>
    chunk.split(/\n{2,}/).map(par => `<p>${escapeHtml(par.trim()).replace(/\n/g, '<br>')}</p>`).join('\n'),
  )
}

const looksLikeHtml = (body) => /<(p|div|h1|h2|table|section|ul)\b/i.test(String(body || ''))

/**
 * The whole document.
 *
 * A body already written as HTML is trusted to know its own pages and is passed
 * through; anything else is broken into paragraphs and pages by the rules above.
 */
export function renderDocument({ body, title, clientName, footerNote, editing = false, look = null } = {}) {
  // A proposal drawn in one of the three looks carries its look on the body,
  // which switches on that look's rules in DOCUMENT_CSS.
  const classes = [editing && 'editing', LOOKS[look] && `look-${look}`].filter(Boolean)
  const html = looksLikeHtml(body)
    ? String(body)
    : textToPages(body)
      .map((page, i) => `<div class="page">
  <div class="runhead">Abstract Creative</div>
  ${page}
  <div class="foot"><span>${escapeHtml(clientName || '')}</span><span class="n">${String(i + 1).padStart(2, '0')}</span></div>
</div>`).join('\n')

  return `<!doctype html>
<html><head><meta charset="utf-8">
<title>${escapeHtml(title || 'Proposal')}</title>
${DOCUMENT_FONTS.map(href => `<link rel="stylesheet" href="${href}">`).join('\n')}
<style>${DOCUMENT_CSS}</style>
</head><body${classes.length ? ` class="${classes.join(' ')}"` : ''}>
${html}
${footerNote ? `<div class="page"><div class="note">${escapeHtml(footerNote)}</div></div>` : ''}
</body></html>`
}

/**
 * Hand the document to the browser to print.
 *
 * A new window rather than an iframe: printing an iframe silently prints the
 * host page in some browsers, and a proposal that prints as the admin console
 * is worse than one that does not print.
 */
export function printDocument(doc, { open = (...a) => window.open(...a) } = {}) {
  const w = open('', '_blank')
  if (!w) return { ok: false, error: 'The browser blocked the print window. Allow pop-ups for the portal and try again.' }
  w.document.write(doc)
  w.document.close()
  w.focus()
  return { ok: true, window: w }
}
